import { ALARM_REPEAT_MS, sosTones } from "./sosAlertRules";

/**
 * The SOS alarm (Web Audio, no sound file): the · · · — — — · · · pattern repeated every few seconds until stop(). Browsers
 * only allow sound after the user has clicked / typed on the page: unlock() is called on the first click or key press of
 * the visit, and an alarm already waiting starts playing at that moment. Without Web Audio (old browser, tests) it does
 * nothing.
 */
export function createSosAlarm() {
  const AudioCtx = typeof window !== "undefined" ? window.AudioContext || window.webkitAudioContext : null;
  let ctx = null;
  let timer = null;

  const context = () => {
    if (!ctx && AudioCtx) ctx = new AudioCtx();
    return ctx;
  };

  const playRound = () => {
    if (!context()) return;
    if (ctx.state === "suspended") {
      // Not allowed yet (no click since the page loaded): play as soon as the browser allows it.
      ctx
        .resume()
        .then(() => {
          if (timer && ctx && ctx.state === "running") playTones();
        })
        .catch(() => {});
      return;
    }
    playTones();
  };

  const playTones = () => {
    const start = ctx.currentTime + 0.05;
    sosTones().forEach(({ at, length }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = 880;
      gain.gain.value = 0.08;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start + at / 1000);
      osc.stop(start + (at + length) / 1000);
    });
  };

  return {
    /** Call from a click / key press: browsers then allow sound for the rest of the visit (and a waiting alarm plays). */
    unlock() {
      if (!context() || ctx.state !== "suspended") return;
      ctx
        .resume()
        .then(() => {
          if (timer) playTones();
        })
        .catch(() => {});
    },
    start() {
      if (timer) return;
      playRound();
      timer = setInterval(playRound, ALARM_REPEAT_MS);
    },
    stop() {
      if (timer) clearInterval(timer);
      timer = null;
    },
    get playing() {
      return Boolean(timer);
    },
    close() {
      this.stop();
      if (ctx) ctx.close().catch(() => {});
      ctx = null;
    },
  };
}
